import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Receipt, Plus, Search, Download, Printer, Trash2, Edit2, X, Check, ArrowLeft, LogOut, Users, Lock, ChevronRight } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/mca`;
const LOGO_URL = '/mca-car-logo.png';

const COMPANY = {
  name: 'MCA CAR',
  address: '34 rue Marcelin Berthelot, 59243 Quarouble',
  vat: 'FR61489040519',
  siret: '489040519000',
  phone: '06 13 85 19 46',
};

const VAT_RATES = [20, 10, 5.5, 0];

const STATUS_LABELS = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  sent: { label: 'Envoyé', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  accepted: { label: 'Accepté', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  refused: { label: 'Refusé', color: 'bg-red-50 text-red-700 border-red-200' },
  pending: { label: 'En attente', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  paid: { label: 'Payée', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

function fmtEur(n) {
  return (Number(n) || 0).toFixed(2).replace('.', ',') + ' €';
}
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('T')[0].split('-');
  return `${d}/${m}/${y}`;
}
function computeTotals(items, discount) {
  let ht = 0, tva = 0;
  (items || []).forEach(it => {
    const line = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
    ht += line;
    tva += line * ((Number(it.vat) || 0) / 100);
  });
  let dAmount = 0;
  if (discount && Number(discount.value) > 0) {
    if (discount.type === 'percent') dAmount = ht * (Number(discount.value) / 100);
    else dAmount = Number(discount.value);
  }
  // Discount applies proportionally on ht+tva
  const ratio = ht > 0 ? (ht - dAmount) / ht : 1;
  const htFinal = ht - dAmount;
  const tvaFinal = tva * ratio;
  return { ht: htFinal, tva: tvaFinal, ttc: htFinal + tvaFinal, gross_ht: ht, discount_amount: dAmount };
}

// ─── LOGIN GATE ───────────────────────────────────────────
function LoginGate({ onLogin }) {
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/login`, { code });
      localStorage.setItem('mca_token', data.token);
      onLogin(data.token);
    } catch (e) { setErr(e.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0F172A' }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm bg-white rounded-3xl p-8 shadow-2xl">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="MCA CAR" className="h-16 mx-auto mb-3 object-contain" />
          <p className="text-sm text-slate-500">Outil de facturation</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider block mb-1.5">Code d&apos;accès</label>
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="password" value={code} onChange={e => setCode(e.target.value)} autoFocus required
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 outline-none focus:border-slate-900 focus:bg-white text-sm"
                placeholder="Entrez votre code" data-testid="mca-login-code" />
            </div>
          </div>
          {err && <p className="text-xs text-red-500">{err}</p>}
          <button type="submit" disabled={loading} className="w-full py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition-all active:scale-95 disabled:opacity-50" data-testid="mca-login-submit">
            {loading ? '...' : 'Accéder'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────
function Dashboard({ token, onNew, onEdit, onLogout }) {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState('all'); // all | quotes | invoices
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [showClients, setShowClients] = useState(false);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/documents`, { ...auth, params: query ? { q: query } : {} });
      setDocs(data.documents || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [query, auth]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === 'quotes') return docs.filter(d => d.type === 'quote');
    if (filter === 'invoices') return docs.filter(d => d.type === 'invoice');
    return docs;
  }, [docs, filter]);

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    await axios.delete(`${API}/documents/${id}`, auth);
    load();
  };

  const convert = async (id) => {
    if (!window.confirm('Transformer ce devis en facture ?')) return;
    const { data } = await axios.post(`${API}/documents/${id}/convert-to-invoice`, {}, auth);
    load();
    alert(`Facture ${data.number} créée !`);
  };

  const stats = useMemo(() => {
    const quotes = docs.filter(d => d.type === 'quote');
    const invoices = docs.filter(d => d.type === 'invoice');
    const paid = invoices.filter(d => d.status === 'paid');
    const pending = invoices.filter(d => d.status === 'pending');
    const paidTotal = paid.reduce((s, d) => s + (d.totals?.ttc || 0), 0);
    const pendingTotal = pending.reduce((s, d) => s + (d.totals?.ttc || 0), 0);
    return { quotes: quotes.length, invoices: invoices.length, paidTotal, pendingTotal, pendingCount: pending.length };
  }, [docs]);

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC' }}>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="MCA CAR" className="h-9 object-contain" />
            <div className="hidden sm:block h-6 w-px bg-slate-200" />
            <span className="hidden sm:block text-sm text-slate-500 font-medium">Facturation</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowClients(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 transition-colors" data-testid="mca-clients-btn">
              <Users className="w-4 h-4" /> <span className="hidden sm:inline">Clients</span>
            </button>
            <button onClick={onLogout} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors" data-testid="mca-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Devis</p>
            <p className="text-2xl sm:text-3xl font-black text-slate-900" data-testid="stat-quotes">{stats.quotes}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200">
            <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1">Factures</p>
            <p className="text-2xl sm:text-3xl font-black text-slate-900" data-testid="stat-invoices">{stats.invoices}</p>
          </div>
          <div className="bg-emerald-50 rounded-2xl p-4 sm:p-5 border border-emerald-200">
            <p className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Encaissé</p>
            <p className="text-lg sm:text-2xl font-black text-emerald-900">{fmtEur(stats.paidTotal)}</p>
          </div>
          <div className="bg-amber-50 rounded-2xl p-4 sm:p-5 border border-amber-200">
            <p className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-1">En attente ({stats.pendingCount})</p>
            <p className="text-lg sm:text-2xl font-black text-amber-900">{fmtEur(stats.pendingTotal)}</p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap gap-3 items-center mb-5">
          <div className="flex gap-1 bg-white rounded-xl p-1 border border-slate-200">
            {[['all', 'Tous'], ['quotes', 'Devis'], ['invoices', 'Factures']].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${filter === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`} data-testid={`filter-${k}`}>{l}</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Rechercher par client ou numéro..."
              className="w-full pl-10 pr-4 py-2 rounded-xl bg-white border border-slate-200 text-sm text-slate-900 outline-none focus:border-slate-900" data-testid="mca-search" />
          </div>
          <button onClick={() => onNew('quote')} className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-sm font-semibold text-slate-700 transition-all active:scale-95" data-testid="new-quote-btn">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nouveau</span> devis
          </button>
          <button onClick={() => onNew('invoice')} className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-all active:scale-95" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nouvelle</span> facture
          </button>
        </div>

        {/* Documents list */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-slate-400 text-sm">Chargement...</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Aucun document pour l&apos;instant</p>
              <p className="text-xs text-slate-400 mt-1">Créez votre premier devis ou facture pour démarrer</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map(doc => {
                const status = STATUS_LABELS[doc.status] || STATUS_LABELS.draft;
                return (
                  <div key={doc.id} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-slate-50 transition-colors group">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${doc.type === 'quote' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-700'}`}>
                      {doc.type === 'quote' ? <FileText className="w-5 h-5" /> : <Receipt className="w-5 h-5" />}
                    </div>
                    <button onClick={() => onEdit(doc)} className="flex-1 min-w-0 text-left" data-testid={`doc-${doc.number}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-slate-900 text-sm">{doc.number}</p>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${status.color}`}>{status.label}</span>
                      </div>
                      <p className="text-sm text-slate-600 truncate mt-0.5">{doc.client_name || 'Sans client'}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">Émis le {fmtDate(doc.issue_date)}</p>
                    </button>
                    <div className="hidden sm:block text-right">
                      <p className="font-black text-slate-900">{fmtEur(doc.totals?.ttc || 0)}</p>
                      <p className="text-[10px] text-slate-400">TTC</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {doc.type === 'quote' && (doc.status === 'accepted' || doc.status === 'sent' || doc.status === 'draft') && (
                        <button onClick={() => convert(doc.id)} title="Convertir en facture" className="p-2 rounded-lg text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 transition-colors" data-testid={`convert-${doc.number}`}>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => remove(doc.id)} className="p-2 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {showClients && <ClientsPanel token={token} onClose={() => setShowClients(false)} />}
    </div>
  );
}

// ─── CLIENTS PANEL ────────────────────────────────────────
function ClientsPanel({ token, onClose }) {
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/clients`, auth);
    setClients(data.clients || []);
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name) return alert('Nom requis');
    if (editing.id) await axios.put(`${API}/clients/${editing.id}`, editing, auth);
    else await axios.post(`${API}/clients`, editing, auth);
    setEditing(null); load();
  };
  const del = async (id) => {
    if (!window.confirm('Supprimer ce client ?')) return;
    await axios.delete(`${API}/clients/${id}`, auth); load();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full sm:max-w-xl max-h-[90vh] bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-2"><Users className="w-5 h-5 text-slate-700" /><h2 className="text-lg font-bold text-slate-900">Clients</h2></div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        {editing ? (
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {[['name', 'Nom / Société *'], ['address', 'Adresse'], ['phone', 'Téléphone'], ['email', 'Email'], ['vat', 'Numéro TVA (optionnel)']].map(([k, l]) => (
              <div key={k}>
                <label className="text-xs font-semibold text-slate-600 block mb-1">{l}</label>
                <input value={editing[k] || ''} onChange={e => setEditing({ ...editing, [k]: e.target.value })} className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none focus:border-slate-900 focus:bg-white" />
              </div>
            ))}
            <div className="flex gap-2 pt-3">
              <button onClick={() => setEditing(null)} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuler</button>
              <button onClick={save} className="flex-1 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold" data-testid="save-client">Enregistrer</button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-slate-100">
              <button onClick={() => setEditing({ name: '', address: '', phone: '', email: '', vat: '' })} className="w-full py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold flex items-center justify-center gap-2" data-testid="new-client-btn">
                <Plus className="w-4 h-4" /> Nouveau client
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {clients.length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-400">Aucun client enregistré</p>
              ) : clients.map(c => (
                <div key={c.id} className="flex items-center gap-3 p-4 border-b border-slate-50 hover:bg-slate-50 group">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-black text-slate-600">{c.name.charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.email || c.phone || c.address || '—'}</p>
                  </div>
                  <button onClick={() => setEditing(c)} className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(c.id)} className="p-2 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

// ─── DOCUMENT EDITOR ──────────────────────────────────────
function DocumentEditor({ token, doc: initialDoc, mode, onClose }) {
  const isNew = !initialDoc.id;
  const [doc, setDoc] = useState(() => ({
    type: initialDoc.type || mode || 'quote',
    number: initialDoc.number || '',
    status: initialDoc.status || (initialDoc.type === 'invoice' ? 'pending' : 'draft'),
    client_id: initialDoc.client_id || '',
    client_name: initialDoc.client_name || '',
    client_address: initialDoc.client_address || '',
    client_phone: initialDoc.client_phone || '',
    client_email: initialDoc.client_email || '',
    client_vat: initialDoc.client_vat || '',
    issue_date: initialDoc.issue_date || new Date().toISOString().split('T')[0],
    due_date: initialDoc.due_date || '',
    items: initialDoc.items?.length ? initialDoc.items : [{ designation: '', qty: 1, unit_price: 0, vat: 20 }],
    discount: initialDoc.discount || { type: 'percent', value: 0 },
    notes: initialDoc.notes || '',
    id: initialDoc.id,
  }));
  const [clients, setClients] = useState([]);
  const [showClientPick, setShowClientPick] = useState(false);
  const [saving, setSaving] = useState(false);

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const isQuote = doc.type === 'quote';

  useEffect(() => {
    axios.get(`${API}/clients`, auth).then(r => setClients(r.data.clients || [])).catch(() => {});
  }, [auth]);

  const totals = useMemo(() => computeTotals(doc.items, doc.discount), [doc.items, doc.discount]);

  const updateItem = (i, k, v) => {
    const items = [...doc.items]; items[i] = { ...items[i], [k]: v }; setDoc({ ...doc, items });
  };
  const addItem = () => setDoc({ ...doc, items: [...doc.items, { designation: '', qty: 1, unit_price: 0, vat: 20 }] });
  const removeItem = (i) => setDoc({ ...doc, items: doc.items.filter((_, idx) => idx !== i) });

  const pickClient = (c) => {
    setDoc({ ...doc, client_id: c.id, client_name: c.name, client_address: c.address, client_phone: c.phone, client_email: c.email, client_vat: c.vat });
    setShowClientPick(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...doc, totals: { ht: totals.ht, tva: totals.tva, ttc: totals.ttc } };
      if (isNew) {
        await axios.post(`${API}/documents`, payload, auth);
      } else {
        await axios.put(`${API}/documents/${doc.id}`, payload, auth);
      }
      onClose(true);
    } catch (e) { alert('Erreur: ' + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const downloadPDF = () => {
    const payload = { ...doc, totals: { ht: totals.ht, tva: totals.tva, ttc: totals.ttc } };
    generatePDF(payload, false);
  };
  const printPDF = () => {
    const payload = { ...doc, totals: { ht: totals.ht, tva: totals.tva, ttc: totals.ttc } };
    generatePDF(payload, true);
  };

  const statuses = isQuote
    ? [['draft', 'Brouillon'], ['sent', 'Envoyé'], ['accepted', 'Accepté'], ['refused', 'Refusé']]
    : [['pending', 'En attente'], ['sent', 'Envoyée'], ['paid', 'Payée']];

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC' }}>
      {/* Toolbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => onClose(false)} className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center flex-shrink-0"><ArrowLeft className="w-4 h-4" /></button>
            <div className="min-w-0">
              <p className="text-xs text-slate-500 truncate">{isQuote ? 'Devis' : 'Facture'} {isNew ? '(nouveau)' : ''}</p>
              <p className="font-bold text-slate-900 text-sm truncate">{doc.number || (isQuote ? 'Nouveau devis' : 'Nouvelle facture')}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {!isNew && (
              <>
                <button onClick={printPDF} className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100" data-testid="print-btn"><Printer className="w-4 h-4" /> <span className="hidden md:inline">Imprimer</span></button>
                <button onClick={downloadPDF} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-800 font-semibold" data-testid="download-pdf-btn"><Download className="w-4 h-4" /> <span className="hidden sm:inline">PDF</span></button>
              </>
            )}
            <button onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50" data-testid="save-doc-btn">
              <Check className="w-4 h-4" /> {saving ? '...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid gap-4 md:grid-cols-3">
          {/* Client card */}
          <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Client</label>
              <button onClick={() => setShowClientPick(true)} className="text-xs text-slate-900 font-semibold hover:underline" data-testid="pick-client-btn">Choisir depuis le carnet</button>
            </div>
            <input value={doc.client_name} onChange={e => setDoc({ ...doc, client_name: e.target.value })} placeholder="Nom / Société" className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm mb-2 outline-none focus:border-slate-900 focus:bg-white" data-testid="client-name-input" />
            <input value={doc.client_address} onChange={e => setDoc({ ...doc, client_address: e.target.value })} placeholder="Adresse" className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm mb-2 outline-none focus:border-slate-900 focus:bg-white" />
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input value={doc.client_phone} onChange={e => setDoc({ ...doc, client_phone: e.target.value })} placeholder="Téléphone" className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-slate-900 focus:bg-white" />
              <input value={doc.client_email} onChange={e => setDoc({ ...doc, client_email: e.target.value })} placeholder="Email" className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-slate-900 focus:bg-white" />
            </div>
            <input value={doc.client_vat} onChange={e => setDoc({ ...doc, client_vat: e.target.value })} placeholder="N° TVA (optionnel)" className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-slate-900 focus:bg-white" />
          </div>

          {/* Dates + Status */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Date d&apos;émission</label>
              <input type="date" value={doc.issue_date} onChange={e => setDoc({ ...doc, issue_date: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-slate-900" />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">{isQuote ? 'Valable jusqu\'au' : 'Échéance'}</label>
              <input type="date" value={doc.due_date} onChange={e => setDoc({ ...doc, due_date: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-slate-900" />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Statut</label>
              <select value={doc.status} onChange={e => setDoc({ ...doc, status: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-slate-900" data-testid="status-select">
                {statuses.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-sm">Lignes</h3>
            <button onClick={addItem} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-xs font-semibold text-slate-700" data-testid="add-item-btn"><Plus className="w-3.5 h-3.5" /> Ajouter</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3 font-semibold">Désignation</th>
                  <th className="text-right p-3 font-semibold w-20">Qté</th>
                  <th className="text-right p-3 font-semibold w-28">P.U. HT</th>
                  <th className="text-right p-3 font-semibold w-24">TVA</th>
                  <th className="text-right p-3 font-semibold w-28">Total HT</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {doc.items.map((it, i) => (
                  <tr key={i}>
                    <td className="p-2"><input value={it.designation} onChange={e => updateItem(i, 'designation', e.target.value)} placeholder="Prestation / produit" className="w-full px-2 py-1.5 rounded bg-transparent hover:bg-slate-50 focus:bg-slate-50 outline-none text-sm" data-testid={`item-${i}-desig`} /></td>
                    <td className="p-2"><input type="number" step="0.01" value={it.qty} onChange={e => updateItem(i, 'qty', parseFloat(e.target.value) || 0)} className="w-full px-2 py-1.5 rounded bg-transparent hover:bg-slate-50 focus:bg-slate-50 outline-none text-sm text-right" /></td>
                    <td className="p-2"><input type="number" step="0.01" value={it.unit_price} onChange={e => updateItem(i, 'unit_price', parseFloat(e.target.value) || 0)} className="w-full px-2 py-1.5 rounded bg-transparent hover:bg-slate-50 focus:bg-slate-50 outline-none text-sm text-right" /></td>
                    <td className="p-2">
                      <select value={it.vat} onChange={e => updateItem(i, 'vat', parseFloat(e.target.value))} className="w-full px-2 py-1.5 rounded bg-transparent hover:bg-slate-50 focus:bg-slate-50 outline-none text-sm text-right">
                        {VAT_RATES.map(r => <option key={r} value={r}>{r} %</option>)}
                      </select>
                    </td>
                    <td className="p-2 text-right font-semibold text-slate-900 text-sm">{fmtEur((it.qty || 0) * (it.unit_price || 0))}</td>
                    <td className="p-2"><button onClick={() => removeItem(i)} className="p-1.5 rounded text-slate-300 hover:bg-red-50 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Notes + Totals */}
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <div className="md:col-span-2 bg-white rounded-2xl border border-slate-200 p-5">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">Notes / Conditions de paiement</label>
            <textarea value={doc.notes} onChange={e => setDoc({ ...doc, notes: e.target.value })} rows={4} className="w-full px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-slate-900 focus:bg-white resize-none" placeholder={isQuote ? "Ex: Devis valable 30 jours..." : "Ex: Paiement à réception..."} />
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="mb-3">
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Remise</label>
              <div className="flex gap-1">
                <input type="number" step="0.01" value={doc.discount.value} onChange={e => setDoc({ ...doc, discount: { ...doc.discount, value: parseFloat(e.target.value) || 0 } })} className="flex-1 px-2 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-sm outline-none focus:border-slate-900" />
                <select value={doc.discount.type} onChange={e => setDoc({ ...doc, discount: { ...doc.discount, type: e.target.value } })} className="px-2 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                  <option value="percent">%</option><option value="fixed">€</option>
                </select>
              </div>
            </div>
            <div className="space-y-1.5 text-sm border-t border-slate-100 pt-3">
              <div className="flex justify-between text-slate-600"><span>Total HT</span><span>{fmtEur(totals.ht)}</span></div>
              <div className="flex justify-between text-slate-600"><span>TVA</span><span>{fmtEur(totals.tva)}</span></div>
              {totals.discount_amount > 0 && <div className="flex justify-between text-emerald-600 text-xs"><span>Remise appliquée</span><span>−{fmtEur(totals.discount_amount)}</span></div>}
              <div className="flex justify-between text-slate-900 font-black text-lg pt-2 border-t border-slate-100"><span>TOTAL TTC</span><span data-testid="total-ttc">{fmtEur(totals.ttc)}</span></div>
            </div>
          </div>
        </div>
      </main>

      {/* Client picker modal */}
      {showClientPick && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={() => setShowClientPick(false)}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Choisir un client</h3>
              <button onClick={() => setShowClientPick(false)} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {clients.length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-400">Aucun client enregistré</p>
              ) : clients.map(c => (
                <button key={c.id} onClick={() => pickClient(c)} className="w-full flex items-center gap-3 p-4 border-b border-slate-50 hover:bg-slate-50 text-left" data-testid={`pick-client-${c.id}`}>
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-black text-slate-600">{c.name.charAt(0).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{c.name}</p>
                    <p className="text-xs text-slate-500 truncate">{c.email || c.phone || '—'}</p>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

// ─── PDF GENERATION ───────────────────────────────────────
async function generatePDF(doc, print = false) {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const isQuote = doc.type === 'quote';

  // Try load logo
  let logoData = null;
  try {
    const resp = await fetch(LOGO_URL);
    const blob = await resp.blob();
    logoData = await new Promise(res => {
      const r = new FileReader(); r.onloadend = () => res(r.result); r.readAsDataURL(blob);
    });
  } catch (e) { console.warn('MCA PDF: could not load logo', e); }

  // Header
  if (logoData) {
    try { pdf.addImage(logoData, 'PNG', 15, 12, 40, 20); } catch (e) { console.warn('MCA PDF: addImage failed', e); }
  } else {
    pdf.setFontSize(20); pdf.setTextColor(15, 23, 42); pdf.setFont('helvetica', 'bold');
    pdf.text(COMPANY.name, 15, 22);
  }

  // Company info right side
  pdf.setFontSize(9); pdf.setTextColor(100, 116, 139); pdf.setFont('helvetica', 'normal');
  const rightX = 195;
  pdf.text(COMPANY.name, rightX, 15, { align: 'right' });
  pdf.text(COMPANY.address, rightX, 20, { align: 'right' });
  pdf.text('Tél : ' + COMPANY.phone, rightX, 25, { align: 'right' });
  pdf.text('SIRET : ' + COMPANY.siret, rightX, 30, { align: 'right' });
  pdf.text('TVA : ' + COMPANY.vat, rightX, 35, { align: 'right' });

  // Title
  pdf.setFontSize(22); pdf.setTextColor(15, 23, 42); pdf.setFont('helvetica', 'bold');
  pdf.text(isQuote ? 'DEVIS' : 'FACTURE', 15, 55);
  pdf.setFontSize(11); pdf.setTextColor(71, 85, 105); pdf.setFont('helvetica', 'normal');
  pdf.text('N° ' + doc.number, 15, 62);

  // Dates
  pdf.setFontSize(9); pdf.setTextColor(71, 85, 105);
  pdf.text('Date d\'émission : ' + fmtDate(doc.issue_date), rightX, 55, { align: 'right' });
  if (doc.due_date) pdf.text((isQuote ? 'Valable jusqu\'au : ' : 'Échéance : ') + fmtDate(doc.due_date), rightX, 60, { align: 'right' });
  if (doc.quote_ref) pdf.text('Réf. devis : ' + doc.quote_ref, rightX, 65, { align: 'right' });

  // Client box
  pdf.setFillColor(248, 250, 252);
  pdf.rect(15, 72, 90, 30, 'F');
  pdf.setFontSize(8); pdf.setTextColor(100, 116, 139); pdf.setFont('helvetica', 'bold');
  pdf.text('FACTURÉ À', 19, 78);
  pdf.setFontSize(11); pdf.setTextColor(15, 23, 42); pdf.setFont('helvetica', 'bold');
  pdf.text(doc.client_name || '—', 19, 84);
  pdf.setFontSize(9); pdf.setTextColor(71, 85, 105); pdf.setFont('helvetica', 'normal');
  let cy = 89;
  if (doc.client_address) { pdf.text(doc.client_address, 19, cy); cy += 4; }
  if (doc.client_phone) { pdf.text('Tél : ' + doc.client_phone, 19, cy); cy += 4; }
  if (doc.client_email) { pdf.text(doc.client_email, 19, cy); cy += 4; }
  if (doc.client_vat) { pdf.text('TVA : ' + doc.client_vat, 19, cy); }

  // Items table
  const body = (doc.items || []).map(it => {
    const line = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
    return [
      it.designation || '—',
      String(it.qty || 0).replace('.', ','),
      fmtEur(it.unit_price || 0),
      (it.vat || 0) + ' %',
      fmtEur(line),
    ];
  });
  autoTable(pdf, {
    startY: 112,
    head: [['Désignation', 'Qté', 'P.U. HT', 'TVA', 'Total HT']],
    body,
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
    margin: { left: 15, right: 15 },
  });

  const t = computeTotals(doc.items, doc.discount);
  let y = pdf.lastAutoTable.finalY + 8;

  // Totals right-aligned
  const totalsX = 130;
  pdf.setFontSize(9); pdf.setTextColor(71, 85, 105); pdf.setFont('helvetica', 'normal');
  pdf.text('Total HT', totalsX, y); pdf.text(fmtEur(t.gross_ht), 195, y, { align: 'right' }); y += 5;
  if (t.discount_amount > 0) { pdf.setTextColor(5, 150, 105); pdf.text('Remise', totalsX, y); pdf.text('−' + fmtEur(t.discount_amount), 195, y, { align: 'right' }); pdf.setTextColor(71, 85, 105); y += 5; }
  pdf.text('TVA', totalsX, y); pdf.text(fmtEur(t.tva), 195, y, { align: 'right' }); y += 6;
  pdf.setDrawColor(226, 232, 240); pdf.line(totalsX, y - 2, 195, y - 2);
  pdf.setFontSize(13); pdf.setTextColor(15, 23, 42); pdf.setFont('helvetica', 'bold');
  pdf.text('TOTAL TTC', totalsX, y + 3); pdf.text(fmtEur(t.ttc), 195, y + 3, { align: 'right' });

  y += 18;

  // Notes
  if (doc.notes) {
    pdf.setFontSize(8); pdf.setTextColor(100, 116, 139); pdf.setFont('helvetica', 'bold');
    pdf.text('NOTES', 15, y); y += 4;
    pdf.setFontSize(9); pdf.setTextColor(71, 85, 105); pdf.setFont('helvetica', 'normal');
    const noteLines = pdf.splitTextToSize(doc.notes, 180);
    pdf.text(noteLines, 15, y);
  }

  // Footer
  pdf.setFontSize(8); pdf.setTextColor(148, 163, 184); pdf.setFont('helvetica', 'normal');
  const footY = 285;
  pdf.setDrawColor(226, 232, 240); pdf.line(15, footY - 4, 195, footY - 4);
  pdf.text(COMPANY.name + '  •  ' + COMPANY.address, 105, footY, { align: 'center' });
  pdf.text('SIRET : ' + COMPANY.siret + '  •  TVA : ' + COMPANY.vat + '  •  Tél : ' + COMPANY.phone, 105, footY + 4, { align: 'center' });

  if (print) {
    pdf.autoPrint();
    window.open(pdf.output('bloburl'), '_blank');
  } else {
    pdf.save(`${doc.number || (isQuote ? 'devis' : 'facture')}.pdf`);
  }
}

// ─── MAIN PAGE ────────────────────────────────────────────
export default function McaCarPage() {
  const [token, setToken] = useState(() => localStorage.getItem('mca_token'));
  const [view, setView] = useState('dashboard'); // dashboard | editor
  const [editingDoc, setEditingDoc] = useState(null);

  const logout = () => { localStorage.removeItem('mca_token'); setToken(null); };

  useEffect(() => {
    // Set page title
    document.title = 'MCA CAR • Facturation';
  }, []);

  if (!token) return (
    <div className="mca-scope">
      <style>{`
        .mca-scope, .mca-scope * { color: #0F172A; }
        .mca-scope input, .mca-scope textarea, .mca-scope select {
          color: #0F172A !important;
          -webkit-text-fill-color: #0F172A;
          caret-color: #0F172A;
        }
        .mca-scope input::placeholder, .mca-scope textarea::placeholder {
          color: #94A3B8 !important;
          -webkit-text-fill-color: #94A3B8;
        }
        .mca-scope input[type="date"]::-webkit-calendar-picker-indicator { filter: none; }
      `}</style>
      <LoginGate onLogin={setToken} />
    </div>
  );

  return (
    <div className="mca-scope">
      <style>{`
        .mca-scope input, .mca-scope textarea, .mca-scope select {
          color: #0F172A !important;
          -webkit-text-fill-color: #0F172A;
          caret-color: #0F172A;
        }
        .mca-scope input::placeholder, .mca-scope textarea::placeholder {
          color: #94A3B8 !important;
          -webkit-text-fill-color: #94A3B8;
        }
      `}</style>
      <AnimatePresence mode="wait">
        {view === 'dashboard' ? (
          <motion.div key="d" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <Dashboard
              token={token}
              onNew={(type) => { setEditingDoc({ type }); setView('editor'); }}
              onEdit={(doc) => { setEditingDoc(doc); setView('editor'); }}
              onLogout={logout}
            />
          </motion.div>
        ) : (
          <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <DocumentEditor
              token={token}
              doc={editingDoc}
              mode={editingDoc?.type}
              onClose={() => { setView('dashboard'); setEditingDoc(null); }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
